import React, { useState } from 'react';

import { Autocomplete, TextField, createFilterOptions } from '@mui/material';

import type { CreatedAsset, RoomAssetLink } from '../packageOptions/types';

import CreateInclusionAssetDialog from './CreateInclusionAssetDialog';
import type { InclusionAssetLink } from './types';

interface Props {
  assets: Array<RoomAssetLink>;
  currency: string;
  onChange: (value: InclusionAssetLink | undefined) => void;
  onCreateAsset: (name: string) => CreatedAsset;
  value: InclusionAssetLink | undefined;
}

// A pseudo-option at the end of the list that opens the create form instead of linking.
type CreateOption = { createFromInput: string };
type Option = RoomAssetLink | CreateOption;

const isCreateOption = (option: Option): option is CreateOption => 'createFromInput' in option;
const filterAssets = createFilterOptions<RoomAssetLink>();

// See docs/business-rules.md#inclusion-assets (linked by id, never by name).
export default function AssetLinkField({ assets, currency, onChange, onCreateAsset, value }: Props) {
  const [createDialogName, setCreateDialogName] = useState<string | null>(null);
  const selectedAsset = assets.find((asset) => asset.id === value?.assetId) ?? null;
  // An inclusion copied from a previous Opportunity can link an asset this account doesn't list.
  const selectedOption: RoomAssetLink | null =
    selectedAsset ?? (value ? { id: value.assetId, name: value.assetName } : null);

  const link = (asset: RoomAssetLink) => onChange({ assetId: asset.id, assetName: asset.name });

  return (
    <>
      <Autocomplete<Option>
        size="small"
        options={assets}
        value={selectedOption}
        getOptionLabel={(option) => (isCreateOption(option) ? option.createFromInput : option.name)}
        isOptionEqualToValue={(option, other) =>
          !isCreateOption(option) && !isCreateOption(other) && option.id === other.id
        }
        filterOptions={(options, state) => [
          ...filterAssets(
            options.filter((option): option is RoomAssetLink => !isCreateOption(option)),
            state,
          ),
          { createFromInput: state.inputValue.trim() },
        ]}
        renderOption={(props, option) => (
          <li {...props} key={isCreateOption(option) ? 'create' : option.id}>
            {isCreateOption(option)
              ? option.createFromInput
                ? `+ Create "${option.createFromInput}"`
                : '+ Create new asset'
              : option.name}
          </li>
        )}
        onChange={(_event, option) => {
          if (!option) {
            onChange(undefined);
          } else if (isCreateOption(option)) {
            setCreateDialogName(option.createFromInput);
          } else {
            link(option);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Asset"
            error={!value}
            required
            helperText={selectedAsset && !selectedAsset.sfid ? `Creates new asset (${currency})` : undefined}
          />
        )}
        sx={{ minWidth: 180, flex: 1 }}
      />
      <CreateInclusionAssetDialog
        assets={assets}
        initialName={createDialogName ?? ''}
        onClose={() => setCreateDialogName(null)}
        onCreate={(name) => link(onCreateAsset(name))}
        onUseExisting={link}
        open={createDialogName !== null}
      />
    </>
  );
}
